export const LOADING_MESSAGES = {
	default: "Loading chart data...",
	filtering: "Applying filter...",
	refreshing: "Refreshing data...",
} as const;

export const ERROR_MESSAGES = {
	networkError: "Failed to load chart data",
	invalidData: "Unable to display chart: Invalid data format",
	generic: "An error occurred while loading this chart",
	retry: "Tap to retry",
} as const;

export const EMPTY_MESSAGES = {
	noData: "No data available for this period",
	noResults: "No results found",
	comingSoon: "Chart data coming soon",
} as const;

export const CHART_FONT_FALLBACK = "system-ui, -apple-system, sans-serif";

export const getChartFontFamily = (appFont?: string): string => {
	if (!appFont) {
		return CHART_FONT_FALLBACK;
	}
	return `${appFont}-regular`;
};

export const CARD_DIMENSIONS = {
	maxWidth: undefined,
	maxHeight: 843,
	elevation: {
		default: 2,
		hover: 4,
	},
} as const;

export const CARD_HEADER = {
	height: 64,
	iconContainerSize: 40,
	iconSize: 24,
	horizontalPadding: 16,
	paddingRight: 8,
	verticalPadding: 8,
	gap: 12,
} as const;

export const INSIGHT_CALLOUT = {
	height: 64,
	iconSize: 24,
	horizontalPadding: 16,
	verticalPadding: 16,
	gap: 12,
	collapsedLines: 2,
	textWrapBasis: 240,
} as const;

export const HEADER_DIMENSIONS = CARD_HEADER;
export const INSIGHT_CALLOUT_DIMENSIONS = INSIGHT_CALLOUT;

export const FILTER_CHIP_BORDER_RADIUS_MULTIPLIER = 2;

export const FILTER_CHIP_DIMENSIONS = {
	height: 32,
	horizontalPadding: 16,
	verticalPadding: 6,
	gap: 8,
} as const;

export const GAUGE_DIMENSIONS = {
	small: {
		size: 40,
		strokeWidth: 2,
		innerTextSize: 8,
		backgroundSize: 21,
	},
	medium: {
		size: 64,
		strokeWidth: 4,
		innerTextSize: 10,
		backgroundSize: 34,
	},
	large: {
		size: 80,
		strokeWidth: 5,
		innerTextSize: 13,
		backgroundSize: 42,
	},
} as const;

export const STREAK_CELL_DIMENSIONS = {
	width: 40,
	height: 60,
	circleSize: 40,
	todayRingStrokeWidth: 2,
	gap: 4,
} as const;

export const LEGEND_BADGE_BORDER_RADIUS_MULTIPLIER = 1;

export const LEGEND_DIMENSIONS = {
	badgeSize: 12,
	iconSize: 24,
	itemGap: 4,
} as const;

export const SPACING = {
	card: 16,
	lg: 16,
	section: 20,
	grid: 16,
	chips: 8,
	legend: 24,
	legendItemGap: 4,
	listItem: 16,
} as const;

export const ANIMATION_DURATION = {
	shimmer: 2000,
	expandCollapse: 300,
	skeletonPulse: 1500,
	tooltipFadeIn: 150,
	tooltipFadeOut: 100,
	bottomSheetSlide: 300,
	hoverElevation: 200,
	modalEnter: 300,
	modalExit: 250,
} as const;

export const SHIMMER_ANIMATION = {
	duration: 2000,
	easing: "linear" as const,
	loop: true,
	opacity: {
		min: 0.5,
		max: 1.0,
	},
} as const;

export const CHART_DIMENSIONS = {
	radarChart: {
		padding: { top: 60, bottom: 60, left: 80, right: 80 },
		axisLabelFontSize: 11,
		tickLabelFontSize: 8,
		strokeWidth: 2,
		dotRadius: 4,
	},
	lineChart: {
		padding: { top: 20, bottom: 40, left: 60, right: 60 },
		strokeWidth: 2,
		dotRadius: 3.3,
		dashedLinePattern: [5, 5],
		axisLabelFontSize: 11,
		tickLabelFontSize: 9.2,
	},
	barChart: {
		padding: { top: 10, bottom: 50, left: 68, right: 56 },
		referenceWidth: 361,
		barWidth: 17,
		barSpacing: 21,
		domainPaddingX: 20,
		cornerRadius: 0,
		axisLabelFontSize: 11,
		tickLabelFontSize: 9.183,
		thresholdLineStrokeWidth: 0.5,
		thresholdLineDash: [3, 3],
	},
	circularProgressList: {
		itemHeight: 56,
		itemMinHeight: 56,
		ringSize: 40,
		innerRadius: 16,
		outerRadius: 20,
		gap: 16,
		labelFontSize: 16,
		countFontSize: 11,
	},
	circularGaugeGrid: {
		gaugeSize: 64,
		innerGaugeSize: 34,
		gaugeCenterOffset: { left: 14.76, top: 17.76 },
		gap: 16,
		maxContainerWidth: 420,
		minGaugeWidth: 64,
		maxGaugeWidth: 96,
		percentageFontSize: 11,
		labelFontSize: 11,
		fractionFontSize: 11,
		legendIconSize: 24,
	},
	dailyStreak: {
		cellSize: 40,
		cellGap: 4,
		chipMaxWidth: 180,
		chipPadding: { left: 8, right: 12, vertical: 8 },
		chipBorderRadius: 12,
		streakStatGap: 12,
	},
	radarChartConfig: {
		gridLevels: 5,
		maxValue: 100,
	},
} as const;

export const GAUGE_SEGMENTS = {
	total: 5,
	arcDegrees: 52,
	gapDegrees: 10,
	bottomGapDegrees: 60,
	backgroundOpacity: 0.15,
} as const;

export const MIN_TOUCH_TARGET = {
	width: 44,
	height: 44,
} as const;

export const ACCESSIBILITY_LABELS = {
	infoButton: "Show information about this data",
	expandCollapse: "Expand to see detailed breakdown",
	filterChip: (label: string) => `Filter by ${label}`,
	legendItem: (label: string) => `Legend: ${label}`,
	closeBottomSheet: "Close detailed insights",
} as const;

export const ICON_NAMES = {
	info: "information" as const,
	expand: "chevron-down" as const,
	collapse: "chevron-up" as const,
	insight: "lightbulb" as const,
	close: "close" as const,
	checkmark: "check" as const,
	error: "alert-circle" as const,
	empty: "chart-line" as const,
	loading: "loading" as const,
} as const;

export const TEXT_VARIANTS = {
	cardTitle: "titleMedium" as const,
	cardSubtitle: "bodySmall" as const,
	legend: "labelSmall" as const,
	insight: "bodySmall" as const,
	filter: "labelMedium" as const,
	metricLabel: "bodyMedium" as const,
	metricValue: "titleMedium" as const,
	percentage: "titleLarge" as const,
} as const;

export const CONFIDENCE_LEGEND = [
	{ icon: "emoticon-sad-outline", colorKey: "lowConfidence", range: "0-25" },
	{
		icon: "emoticon-neutral-outline",
		colorKey: "moderateConfidence",
		range: "26-50",
	},
	{
		icon: "emoticon-happy-outline",
		colorKey: "satisfactoryConfidence",
		range: "51-75",
	},
	{
		icon: "emoticon-excited-outline",
		colorKey: "highConfidence",
		range: "76-100",
	},
] as const;

export type ConfidenceLegendColorKey =
	(typeof CONFIDENCE_LEGEND)[number]["colorKey"];

export const OVERALL_SUMMARY_BORDER_RADIUS_MULTIPLIER = 4;

export const OVERALL_SUMMARY_DIMENSIONS = {
	padding: 16,
	gaugeSize: 80,
	metricGap: 20,
	sectionGap: 16,
} as const;
